import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  MunilyAreaIconPickerModalData,
  MunilyAreaIconPickerModalResult,
} from 'src/app/interface/components/forms/area-icon-picker-modal.interface';
import { ImageArea } from 'src/app/interface/area.inteface';
import { take } from 'rxjs/operators';
import { AreaV2AreaImageApiService } from 'src/app/pages/areas/new-area-v2/services/area-v2-area-image-api.service';

const DEFAULT_AREA_IMAGE_ID = '__default_area_image__';
const DEFAULT_AREA_IMAGE_URL = 'https://munily-public-cdn.s3.us-east-1.amazonaws.com/area/area.png';

@Component({
  selector: 'common-area-icon-picker-modal',
  templateUrl: './area-icon-picker-modal.component.html',
  styleUrls: ['./area-icon-picker-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AreaIconPickerModalComponent {
  selectedId: string | null = null;
  readonly filterControl = new FormControl('');
  images: ImageArea[] = [];
  loading = true;

  constructor(
    private readonly dialogRef: MatDialogRef<AreaIconPickerModalComponent, MunilyAreaIconPickerModalResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public readonly data: MunilyAreaIconPickerModalData,
    private readonly translate: TranslateService,
    private readonly areaImagesApi: AreaV2AreaImageApiService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.selectedId = data.selectedId ?? DEFAULT_AREA_IMAGE_ID;

    this.areaImagesApi
      .listActiveImages()
      .pipe(take(1))
      .subscribe({
        next: (list: any) => {
          const fromApi: ImageArea[] = Array.isArray(list) ? list : [];

          const defaultItem: ImageArea = {
            id: DEFAULT_AREA_IMAGE_ID,
            name: 'area',
            ico: '',
            url: DEFAULT_AREA_IMAGE_URL,
            urlv3: DEFAULT_AREA_IMAGE_URL,
            status: 'active',
          };

          const hasDefaultAlready = fromApi.some((img) => this.isSelected(defaultItem) || img?.id === DEFAULT_AREA_IMAGE_ID);
          this.images = hasDefaultAlready ? fromApi : [defaultItem, ...fromApi];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.images = [
            {
              id: DEFAULT_AREA_IMAGE_ID,
              name: 'area',
              ico: '',
              url: DEFAULT_AREA_IMAGE_URL,
              urlv3: DEFAULT_AREA_IMAGE_URL,
              status: 'active',
            },
          ];
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  get filteredImages(): ImageArea[] {
    const q = String(this.filterControl.value ?? '').trim().toLowerCase();
    if (!q) return this.images;
    return this.images.filter((img) => String(img?.name ?? '').toLowerCase().includes(q));
  }

  getImageTitle(img: ImageArea): string {
    const name = String(img?.name ?? '').trim();
    const nameKey = `areasV2.newArea.iconPicker.name.${name}`;

    const translatedName = this.translateOrFallback(nameKey, name);
    return translatedName;
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = String(this.translate.instant(key) ?? '').trim();
    if (!translated || translated === key) return fallback;
    return translated;
  }

  onClose(): void {
    this.dialogRef.close(undefined);
  }

  onSelect(img: ImageArea): void {
    this.selectedId = img.id ?? null;
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  onSave(): void {
    const selected = this.images.find((img) => this.isSelected(img));
    this.dialogRef.close(selected);
  }

  isSelected(img: ImageArea): boolean {
    const selected = this.selectedId;
    if (!selected) return false;
    return selected === img.id || selected === img.ico || selected === img.urlv3 || selected === img.url;
  }

  trackByImageId(_index: number, img: ImageArea): string {
    return img.id || img.ico || img.urlv3 || img.url || String(_index);
  }
}

